import React, { useMemo, useState } from 'react';
import { CAPITALES, EMPLEO_SIN_EMPRESA, aIso, deIso } from './columnas';
import { BuroCombobox } from './BuroCombobox';

const FIJAS_COLONIA = [{ value: '__otra__', label: 'Otra…' }];

// Valores únicos y ordenados de una columna en todas las filas del reporte.
const delReporte = (filas, k) => [...new Set((filas || []).map((f) => (f.valores || {})[k]).filter(Boolean))].sort();

const soloDigitos = (v, max) => String(v).replace(/\D/g, '').slice(0, max);

/*
 * Campo de captura según el tipo de columna (lo usan el editor de celda y la
 * ficha del cliente): catálogo → "escribe y elige" (BuroCombobox, sin ventana
 * nativa), fecha → selector de fecha (se guarda como DDMMAAAA), monto/entero →
 * número, C.P. y teléfono → solo dígitos, municipio/ciudad/empresa → el mismo
 * campo en modo libre (la lista sugiere y lo escrito es el valor).
 *
 * props: col (columnas.js) · value · onChange(valor) · filas (para sugerir lo
 * que ya aparece en el reporte) · estado (clave de Buró del domicilio, pone su
 * capital primero) · colonias (lista del catálogo por C.P.: la colonia pasa a
 * lista con "Otra…" para escribir) · className
 */
export const BuroCampo = ({ col, value, onChange, filas, estado, colonias, disabled = false, invalid = false, className = 'bu-input' }) => {
  // Obligatorio vacío: la clase `bu-req-falta` (borde rojo) la pone quien captura; aquí solo el aria.
  const aria = invalid ? { 'aria-invalid': 'true' } : {};
  // "Otra…": la colonia no está en el catálogo y se escribe a mano.
  const [otra, setOtra] = useState(() => !!value && !!colonias && !colonias.includes(value));
  const lista = useMemo(() => {
    if (col.tipo === 'empresa') return [...new Set([...EMPLEO_SIN_EMPRESA, ...delReporte(filas, '26')])];
    if (col.tipo !== 'lugar') return [];
    const capital = CAPITALES[estado];
    return [...new Set([capital, ...delReporte(filas, col.k), ...Object.values(CAPITALES)].filter(Boolean))];
  }, [col, filas, estado]);

  if ((col.k === '19' || col.dom === 'colonia') && colonias && colonias.length > 0 && !otra) {
    // Mismo campo de "escribe y elige" que la actividad: la lista se acota mientras se escribe.
    const opcionesColonia = [...(col.dom === 'colonia' && value && !colonias.includes(value) ? [value] : []), ...colonias].map((c) => ({ value: c, label: c }));
    return (
      <BuroCombobox
        className={className} invalid={invalid} disabled={disabled}
        ariaLabel="Colonia" placeholder="Escribe para buscar la colonia…"
        options={opcionesColonia}
        fijas={FIJAS_COLONIA}
        value={colonias.includes(value) || (col.dom === 'colonia' && value) ? value : ''}
        onChange={(v) => { if (v === '__otra__') { setOtra(true); onChange(''); } else onChange(v); }}
      />
    );
  }
  if ((col.k === '19' || col.dom === 'colonia') && colonias && colonias.length > 0 && otra) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <input className={className} {...aria} value={value} placeholder="Escribe la colonia" onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="bu-btn bu-btn-link" style={{ height: 36 }} onClick={() => { setOtra(false); onChange(''); }}>Ver lista</button>
      </div>
    );
  }
  // Todos los catálogos (cortos o largos) usan el mismo campo de "escribe y elige".
  if (col.opts) {
    return (
      <BuroCombobox
        className={className} invalid={invalid} disabled={disabled}
        ariaLabel={col.label || 'Catálogo'} placeholder="Escribe para buscar…"
        options={col.opts.map(([v, l]) => ({ value: v, label: l }))}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (col.tipo === 'fecha') {
    return <input type="date" disabled={disabled} className={className} {...aria} {...aria} value={aIso(value)} onChange={(e) => onChange(deIso(e.target.value))} />;
  }
  if (col.kind === 'amount' || col.kind === 'int' || col.kind === 'decimal') {
    return <input type="number" disabled={disabled} min="0" step={col.kind === 'int' ? '1' : col.kind === 'decimal' ? '0.01' : 'any'} className={className} {...aria} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  if (col.tipo === 'cp' || col.tipo === 'tel') {
    const max = col.tipo === 'cp' ? 5 : 10;
    return (
      <input
        inputMode="numeric"
        maxLength={max}
        placeholder={`${max} dígitos`}
        className={className}
        {...aria}
        value={value}
        onChange={(e) => onChange(soloDigitos(e.target.value, max))}
      />
    );
  }
  if (col.tipo === 'lugar' || col.tipo === 'empresa') {
    // Lo escrito ES el valor; la lista solo sugiere.
    return (
      <BuroCombobox
        libre
        className={className} invalid={invalid} disabled={disabled}
        ariaLabel={col.label}
        placeholder="Elige o escribe otro"
        options={lista.map((x) => ({ value: x, label: x }))}
        value={value}
        onChange={onChange}
      />
    );
  }
  if (col.tipo === 'correo') {
    return <input type="email" className={className} {...aria} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  if (col.tipo === 'rfc') {
    return <input maxLength={col.maxLength || 13} className={className} {...aria} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />;
  }
  return <input disabled={disabled} maxLength={col.maxLength} className={className} {...aria} value={value} onChange={(e) => onChange(e.target.value)} />;
};

export default BuroCampo;

/*
 * Aviso corto del catálogo por C.P. (`cp` es el resultado de useCodigoPostal).
 * El autollenado lo hace quien captura; aquí solo se avisa si no se encontró.
 */
export const AvisoCodigoPostal = ({ cp }) => {
  if (cp.estado === 'no_encontrado') {
    return <div className="bu-sub" style={{ fontSize: 11, marginTop: 4 }}>C.P. no encontrado en el catálogo</div>;
  }
  if (cp.estado === 'cargando') return <div className="bu-sub" style={{ fontSize: 11, marginTop: 4 }}>Buscando C.P.…</div>;
  return null;
};
